import { useCallback, useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { Button } from '@frontend/design-system';

const SCROLL_THRESHOLD = 300;

const ScrollToTop = () => {
  const [isVisible, setIsVisible] = useState(false);
  // Track previous visibility to avoid redundant state updates on every scroll event
  const prevVisibleRef = useRef(false);

  useEffect(() => {
    const onScroll = () => {
      const shouldBeVisible = window.scrollY > SCROLL_THRESHOLD;
      // Only update state when visibility actually changes to minimize re-renders
      if (shouldBeVisible !== prevVisibleRef.current) {
        prevVisibleRef.current = shouldBeVisible;
        setIsVisible(shouldBeVisible);
      }
    };
    // passive: true tells the browser this handler won't call preventDefault(), enabling scroll optimizations
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleClick = useCallback(() => window.scrollTo({ top: 0, behavior: 'smooth' }), []);

  // Hide the button until the user has scrolled past the threshold
  if (!isVisible) return null;

  return (
    <Button aria-label='Scroll to top' css={buttonStyle} onClick={handleClick} variant='primary' iconName='caret-up' />
  );
};

export default ScrollToTop;

const buttonStyle = css`
  position: fixed;
  bottom: var(--fabric-space-gap-xl);
  right: var(--fabric-space-gap-xl);
  z-index: 200;
  width: var(--fabric-number-n-10-px);
  height: var(--fabric-number-n-10-px);
  border-radius: 50%;
  box-shadow: 0 var(--fabric-number-n-2-px) var(--fabric-number-n-3-px) var(--fabric-number-n-1-5-px)
      rgba(0, 33, 82, 0.15),
    0 var(--fabric-number-n-1-px) var(--fabric-number-n-1-px) 0 rgba(0, 0, 0, 0.25);
  transition: transform 150ms ease;
  background-color: var(--fabric-color-surface-inverted-default);

  &:hover:not([disabled]) {
    transform: translateY(-2px);
    background-color: var(--fabric-color-surface-inverted-default);
  }

  &:active:not([disabled]) {
    transform: translateY(0);
    background-color: var(--fabric-color-surface-inverted-default);
  }
`;
